import React, { useState } from 'react';
import { Box, Grid, Typography, Popover, Button, InputLabel, TextField, Select, MenuItem, Checkbox, FormControlLabel } from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import { Formik, Form, Field } from 'formik';

interface SelectComponentProps {
  values: any;
  index: number;
  childIndex: number;
  setFieldValue: (key: string, value: any) => void;
  onSubmit: (data: any) => void;
}

export default function SelectComponent({ values, index, childIndex, setFieldValue, onSubmit }: SelectComponentProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [newOption, setNewOption] = useState('');

  const handleEditClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'edit-select-popover' : undefined;

  const field = values.fields[index];
  const child = values.fields[index].children[childIndex];
  const options = field.options || ['Option 1', 'Option 2'];
  const settings = field.settings || {};
  const isMultiSelect = settings.multiSelect || false;

  let answer = field.answer;
  if (isMultiSelect && !Array.isArray(answer)) {
    answer = [];
  } else if (!isMultiSelect && typeof answer !== 'string') {
    answer = '';
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={12}>
          <InputLabel sx={{ color: 'black' }}>{child.question}</InputLabel>
        </Grid>

        <Grid item xs={11}>
          <Select
            fullWidth
            multiple={isMultiSelect}
            value={answer}
            onChange={(e) => setFieldValue(`fields.${index}.answer`, e.target.value)}
            displayEmpty
            renderValue={(selected: any) =>
              isMultiSelect ? selected.length === 0 ? <em>Select options</em> : selected.join(', ') : selected || <em>Select an option</em>
            }
          >
            {options.map((option: string, i: number) => (
              <MenuItem key={i} value={option}>
                {isMultiSelect && <Checkbox checked={answer.includes(option)} />}
                <Typography variant="body2">{option}</Typography>
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={1}>
          <Box sx={{ cursor: 'pointer', color: 'primary.main' }} onClick={handleEditClick}>
            <CreateIcon fontSize="medium" />
          </Box>
        </Grid>
      </Grid>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ sx: { width: 350, p: 2 } }}
        disableRestoreFocus
      >
        <Formik
          initialValues={{
            question: child.question || '',
            description: child.description || '',
            options: [...(child.options || [])],
            required: settings.required || false,
            multiSelect: settings.multiSelect || false,
            isSupportFileRequired: settings.isSupportFileRequired || false,
            noOfSupportImages: settings.noOfSupportImages || ''
          }}
          onSubmit={(formValues) => {
            const updatedChild = {
              ...child,
              question: formValues.question,
              description: formValues.description,
              options: formValues.options,
              settings: {
                ...settings,
                required: formValues.required,
                multiSelect: formValues.multiSelect,
                isSupportFileRequired: formValues.isSupportFileRequired,
                noOfSupportImages: formValues.isSupportFileRequired ? formValues.noOfSupportImages : ''
              }
            };
            setFieldValue(`fields.${index}.children.${childIndex}`, updatedChild);
            handleClosePopover();
          }}
        >
          {({ values: formValues, handleChange, setFieldValue: setFormikField }) => (
            <Form>
              <Typography variant="subtitle1" mb={2}>
                Edit Select Settings
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <InputLabel>Label</InputLabel>
                </Grid>
                <Grid item xs={8}>
                  <Field as={TextField} name="question" fullWidth size="small" />
                </Grid>

                <Grid item xs={4}>
                  <InputLabel>Description</InputLabel>
                </Grid>
                <Grid item xs={8}>
                  <Field as={TextField} name="description" fullWidth size="small" />
                </Grid>

                <Grid item xs={12}>
                  <InputLabel>Options</InputLabel>
                  {formValues.options.map((opt: string, i: number) => (
                    <Box key={i} display="flex" alignItems="center" mt={1}>
                      <TextField
                        fullWidth
                        size="small"
                        value={opt}
                        onChange={(e) => {
                          const updated = [...formValues.options];
                          updated[i] = e.target.value;
                          setFormikField('options', updated);
                        }}
                      />
                      <Button
                        size="small"
                        color="error"
                        sx={{ ml: 1 }}
                        onClick={() => {
                          const filtered = formValues.options.filter((_, idx) => idx !== i);
                          setFormikField('options', filtered);
                        }}
                      >
                        Delete
                      </Button>
                    </Box>
                  ))}

                  <Box mt={2} display="flex">
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="New Option"
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1 }}
                      onClick={() => {
                        if (newOption.trim() !== '') {
                          setFormikField('options', [...formValues.options, newOption.trim()]);
                          setNewOption('');
                        }
                      }}
                    >
                      Add
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox checked={formValues.required} onChange={(e) => setFormikField('required', e.target.checked)} />}
                    label="Required"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox checked={formValues.multiSelect} onChange={(e) => setFormikField('multiSelect', e.target.checked)} />
                    }
                    label="Allow multiple selections"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox name="isSupportFileRequired" checked={formValues.isSupportFileRequired} onChange={handleChange} />}
                    label="Support File Required"
                  />
                </Grid>

                {formValues.isSupportFileRequired && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="noOfSupportImages"
                      type="number"
                      label="Number of Images"
                      value={formValues.noOfSupportImages}
                      onChange={handleChange}
                      size="small"
                      inputProps={{ min: 1 }}
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button type="submit" size="small" variant="contained">
                      Save
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Popover>
    </Box>
  );
}
